import { useState, useMemo, useEffect } from "react";
import { useRole } from "@/data/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  type AutomationRule, type AutomationExecution, type Reminder,
  type RuleCondition, type RuleActionDef, type AutomationEventType,
  type AutomationActionType, type ConditionField, type ConditionOperator, type RuleStatus,
  DEFAULT_AUTOMATION_RULES,
  EVENT_TYPE_LABELS, ACTION_TYPE_LABELS, CONDITION_FIELD_LABELS, CONDITION_OPERATOR_LABELS,
  TARGET_OPTIONS, createEmptyRule, duplicateRule,
} from "@/data/automationEngine";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Zap, Bell, Clock, CheckCircle2, XCircle, AlertTriangle,
  Settings2, Eye, MessageSquare, Target, Briefcase,
  BarChart3, ChevronRight, Plus, Copy, Archive, Trash2,
  Pencil, MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { getCurrentRepName, normalizeName } from "@/lib/repIdentity";

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  sales: { label: "Sales", icon: Target, color: "text-info bg-info/10 border-info/30" },
  quotes: { label: "Quotes", icon: Briefcase, color: "text-primary bg-primary/10 border-primary/30" },
  operations: { label: "Operations", icon: Settings2, color: "text-warning bg-warning/10 border-warning/30" },
  communication: { label: "Comms", icon: MessageSquare, color: "text-success bg-success/10 border-success/30" },
};

const ACTION_ICONS: Record<string, React.ElementType> = {
  send_notification: Bell,
  send_sms: MessageSquare,
  create_reminder: Clock,
  update_flag: AlertTriangle,
  assign_task: Target,
};

type RuleEditorMode = "create" | "edit";

export default function AutomationDashboard() {
  const { role } = useRole();
  const { user } = useAuth();
  const [rules, setRules] = useState<AutomationRule[]>(DEFAULT_AUTOMATION_RULES);
  const [executions] = useState<AutomationExecution[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [selectedExec, setSelectedExec] = useState<AutomationExecution | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [reminderFilter, setReminderFilter] = useState<string>("pending");

  // Rule editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<RuleEditorMode>("create");
  const [editingRule, setEditingRule] = useState<AutomationRule>(createEmptyRule());
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const isRepRole = role === "sales";
  const currentRep = useMemo(() => {
    if (!isRepRole) return null;
    const resolved = getCurrentRepName(user);
    return resolved || null;
  }, [isRepRole, user]);
  const normalizedCurrentRep = normalizeName(currentRep);

  const stats = useMemo(() => ({
    totalRules: rules.filter((r) => r.status !== "archived").length,
    activeRules: rules.filter((r) => r.enabled && r.status === "active").length,
    archivedRules: rules.filter((r) => r.status === "archived").length,
    totalExecutions: executions.length,
    pendingReminders: reminders.filter((r) => r.status === "pending").length,
  }), [rules, executions, reminders]);

  const filteredRules = useMemo(() => {
    let result = rules;
    if (statusFilter === "active") result = result.filter((r) => r.status === "active");
    else if (statusFilter === "inactive") result = result.filter((r) => r.status === "inactive" || (r.status === "active" && !r.enabled));
    else if (statusFilter === "archived") result = result.filter((r) => r.status === "archived");
    if (categoryFilter !== "all") result = result.filter((r) => r.category === categoryFilter);
    return result.sort((a, b) => b.priority - a.priority);
  }, [rules, categoryFilter, statusFilter]);

  const filteredReminders = useMemo(() => {
    let result = reminders;
    if (isRepRole && normalizedCurrentRep) {
      result = result.filter((r) => normalizeName(r.assignedTo) === normalizedCurrentRep);
    }
    if (reminderFilter !== "all") result = result.filter((r) => r.status === reminderFilter);
    return result.sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());
  }, [reminders, reminderFilter, isRepRole, normalizedCurrentRep]);

  // ─── Rule CRUD ─────────────────────────────────────────────────────────────

  function openCreateRule() {
    setEditingRule(createEmptyRule());
    setEditorMode("create");
    setEditorOpen(true);
  }

  function openEditRule(rule: AutomationRule) {
    setEditingRule({ ...rule, conditions: [...rule.conditions], actions: rule.actions.map((a) => ({ ...a })) });
    setEditorMode("edit");
    setEditorOpen(true);
  }

  function handleDuplicate(rule: AutomationRule) {
    const dup = duplicateRule(rule);
    setRules((prev) => [...prev, dup]);
    toast.success(`Rule duplicated: ${dup.name}`);
  }

  function handleArchive(ruleId: string) {
    setRules((prev) => prev.map((r) => r.id === ruleId ? { ...r, status: "archived" as RuleStatus, enabled: false, updatedAt: new Date().toISOString(), updatedBy: "Admin" } : r));
    toast.success("Rule archived");
  }

  function handleRestore(ruleId: string) {
    setRules((prev) => prev.map((r) => r.id === ruleId ? { ...r, status: "inactive" as RuleStatus, updatedAt: new Date().toISOString(), updatedBy: "Admin" } : r));
    toast.success("Rule restored");
  }

  function handleDelete(ruleId: string) {
    setRules((prev) => prev.filter((r) => r.id !== ruleId));
    setDeleteConfirm(null);
    toast.success("Rule deleted");
  }

  function toggleRule(ruleId: string) {
    setRules((prev) => prev.map((r) => {
      if (r.id !== ruleId) return r;
      const newEnabled = !r.enabled;
      return { ...r, enabled: newEnabled, status: newEnabled ? "active" as RuleStatus : "inactive" as RuleStatus, updatedAt: new Date().toISOString(), updatedBy: "Admin" };
    }));
    const rule = rules.find((r) => r.id === ruleId);
    toast.success(`${rule?.name} ${rule?.enabled ? "disabled" : "enabled"}`);
  }

  function saveRule() {
    if (!editingRule.name.trim()) { toast.error("Rule name is required"); return; }
    if (editingRule.actions.length === 0) { toast.error("At least one action is required"); return; }
    if (editingRule.actions.some((a) => !a.template.trim())) { toast.error("All actions need a message template"); return; }

    const ts = new Date().toISOString();
    if (editorMode === "create") {
      setRules((prev) => [...prev, { ...editingRule, createdAt: ts, updatedAt: ts }]);
      toast.success("Rule created");
    } else {
      setRules((prev) => prev.map((r) => r.id === editingRule.id ? { ...editingRule, updatedAt: ts, updatedBy: "Admin" } : r));
      toast.success("Rule updated");
    }
    setEditorOpen(false);
  }

  // ─── Condition helpers ─────────────────────────────────────────────────────

  function addCondition() {
    setEditingRule((r) => ({ ...r, conditions: [...r.conditions, { id: `cond-${Date.now()}`, field: "lead_source" as ConditionField, operator: "equals" as ConditionOperator, value: "" }] }));
  }
  function removeCondition(condId: string) {
    setEditingRule((r) => ({ ...r, conditions: r.conditions.filter((c) => c.id !== condId) }));
  }
  function updateCondition(condId: string, updates: Partial<RuleCondition>) {
    setEditingRule((r) => ({ ...r, conditions: r.conditions.map((c) => c.id === condId ? { ...c, ...updates } : c) }));
  }

  // ─── Action helpers ────────────────────────────────────────────────────────

  function addAction() {
    setEditingRule((r) => ({ ...r, actions: [...r.actions, { id: `a-${Date.now()}`, type: "send_notification" as AutomationActionType, template: "", target: "assigned_rep" }] }));
  }
  function removeAction(actId: string) {
    setEditingRule((r) => ({ ...r, actions: r.actions.filter((a) => a.id !== actId) }));
  }
  function updateAction(actId: string, updates: Partial<RuleActionDef>) {
    setEditingRule((r) => ({ ...r, actions: r.actions.map((a) => a.id === actId ? { ...a, ...updates } : a) }));
  }

  function completeReminder(remId: string) {
    setReminders((prev) => prev.map((r) => r.id === remId ? { ...r, status: "completed" as const, completedAt: new Date().toISOString() } : r));
    toast.success("Reminder completed");
  }
  function dismissReminder(remId: string) {
    setReminders((prev) => prev.map((r) => r.id === remId ? { ...r, status: "dismissed" as const } : r));
    toast.success("Reminder dismissed");
  }

  function getEntityLink(entityType: string, entityId: string): string {
    switch (entityType) { case "lead": return `/pipeline/${entityId}`; case "quote": return `/opportunities/${entityId}`; case "job": return `/jobs/${entityId}`; default: return "#"; }
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" /> Automation Engine
          </h1>
          <p className="text-sm text-muted-foreground">
            {stats.activeRules} active rules · {stats.pendingReminders} pending reminders · {stats.totalExecutions} executions
          </p>
        </div>
        <Button size="sm" onClick={openCreateRule}><Plus className="h-4 w-4 mr-1" /> New Rule</Button>
      </div>

      <Tabs defaultValue="rules" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rules" className="gap-1"><Settings2 className="h-3.5 w-3.5" /> Rules</TabsTrigger>
          <TabsTrigger value="reminders" className="gap-1">
            <Clock className="h-3.5 w-3.5" /> Reminders
            {stats.pendingReminders > 0 && <Badge variant="destructive" className="ml-1 h-4 px-1 text-[10px]">{stats.pendingReminders}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-1"><BarChart3 className="h-3.5 w-3.5" /> Log</TabsTrigger>
        </TabsList>

        {/* ─── RULES TAB ─── */}
        <TabsContent value="rules" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1">
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                const catRules = rules.filter((r) => r.category === key && r.status !== "archived");
                const active = catRules.filter((r) => r.enabled).length;
                return (
                  <Card key={key} className={cn("border cursor-pointer hover:shadow-sm transition-shadow", categoryFilter === key && "ring-2 ring-primary")} onClick={() => setCategoryFilter(categoryFilter === key ? "all" : key)}>
                    <CardContent className="p-2.5">
                      <div className="flex items-center gap-1.5">
                        <cfg.icon className={cn("h-3.5 w-3.5", cfg.color.split(" ")[0])} />
                        <span className="text-[10px] text-muted-foreground uppercase">{cfg.label}</span>
                      </div>
                      <p className="text-lg font-bold text-foreground">{active}<span className="text-xs font-normal text-muted-foreground">/{catRules.length}</span></p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredRules.length === 0 ? (
            <Card><CardContent className="p-8 text-center">
              <Zap className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="font-medium text-foreground">No rules found</p>
              <p className="text-sm text-muted-foreground mt-1">Try a different filter or create a new rule</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {filteredRules.map((rule) => {
                const catCfg = CATEGORY_CONFIG[rule.category];
                const isArchived = rule.status === "archived";
                return (
                  <Card key={rule.id} className={cn("border transition-all", !rule.enabled && "opacity-60", isArchived && "bg-muted/30")}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {!isArchived && <Switch checked={rule.enabled} onCheckedChange={() => toggleRule(rule.id)} className="mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <p className="text-sm font-medium text-foreground">{rule.name}</p>
                            <Badge variant="outline" className={cn("text-[10px] h-4", catCfg?.color)}>{catCfg?.label}</Badge>
                            {isArchived && <Badge variant="outline" className="text-[10px] h-4 text-muted-foreground">Archived</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">{rule.description}</p>

                          {/* When / If / Then display */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 text-xs">
                              <Badge className="bg-info/15 text-info border-info/30 text-[10px] font-semibold px-1.5">WHEN</Badge>
                              <span className="text-muted-foreground">{EVENT_TYPE_LABELS[rule.eventType]}</span>
                            </div>
                            {rule.conditions.length > 0 && (
                              <div className="flex items-start gap-1.5 text-xs">
                                <Badge className="bg-warning/15 text-warning border-warning/30 text-[10px] font-semibold px-1.5 shrink-0 mt-0.5">IF</Badge>
                                <div className="flex flex-wrap gap-1">
                                  {rule.conditions.map((c) => (
                                    <Badge key={c.id} variant="outline" className="text-[10px]">
                                      {CONDITION_FIELD_LABELS[c.field]} {CONDITION_OPERATOR_LABELS[c.operator]} {c.value && `"${c.value}"`}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="flex items-start gap-1.5 text-xs">
                              <Badge className="bg-success/15 text-success border-success/30 text-[10px] font-semibold px-1.5 shrink-0 mt-0.5">THEN</Badge>
                              <div className="flex flex-wrap gap-1">
                                {rule.actions.map((action) => {
                                  const ActionIcon = ACTION_ICONS[action.type] || Bell;
                                  return (
                                    <Badge key={action.id} variant="outline" className="text-[10px] gap-0.5">
                                      <ActionIcon className="h-2.5 w-2.5" />
                                      {ACTION_TYPE_LABELS[action.type]}
                                      {action.delayHours ? <span className="text-muted-foreground">+{action.delayHours}h</span> : null}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Audit info */}
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                            <span>Updated {new Date(rule.updatedAt).toLocaleDateString()} by {rule.updatedBy}</span>
                            <span>Triggered {rule.triggerCount}×</span>
                            {rule.lastTriggeredAt && <span>Last: {rule.lastTriggeredEntity} ({new Date(rule.lastTriggeredAt).toLocaleDateString()})</span>}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          <div className="text-center mr-1">
                            <p className={cn("text-sm font-bold", rule.priority >= 8 ? "text-destructive" : rule.priority >= 5 ? "text-warning" : "text-muted-foreground")}>{rule.priority}</p>
                            <p className="text-[9px] text-muted-foreground">Priority</p>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditRule(rule)}><Pencil className="h-3.5 w-3.5 mr-2" /> Edit</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicate(rule)}><Copy className="h-3.5 w-3.5 mr-2" /> Duplicate</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {isArchived ? (
                                <DropdownMenuItem onClick={() => handleRestore(rule.id)}><Archive className="h-3.5 w-3.5 mr-2" /> Restore</DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handleArchive(rule.id)}><Archive className="h-3.5 w-3.5 mr-2" /> Archive</DropdownMenuItem>
                              )}
                              <DropdownMenuItem className="text-destructive" onClick={() => setDeleteConfirm(rule.id)}><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ─── REMINDERS TAB ─── */}
        <TabsContent value="reminders" className="space-y-4">
          <div className="flex items-center gap-3">
            <Select value={reminderFilter} onValueChange={setReminderFilter}>
              <SelectTrigger className="w-[150px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{filteredReminders.length} reminders</p>
          </div>
          {filteredReminders.length === 0 ? (
            <Card><CardContent className="p-8 text-center">
              <CheckCircle2 className="h-10 w-10 text-success mx-auto mb-3" />
              <p className="font-medium text-foreground">All Clear</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {filteredReminders.map((rem) => {
                const overdue = rem.status === "pending" && new Date(rem.dueAt) < new Date();
                return (
                  <Card key={rem.id} className={cn("border", overdue && "border-destructive/40 bg-destructive/30")}>
                    <CardContent className="p-3 flex items-start gap-3">
                      <div className={cn("mt-0.5 p-1.5 rounded-md", rem.status === "completed" ? "bg-success/15" : overdue ? "bg-destructive/15" : "bg-warning/15")}>
                        {rem.status === "completed" ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Clock className={cn("h-4 w-4", overdue ? "text-destructive" : "text-warning")} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn("text-sm font-medium", rem.status === "completed" && "line-through text-muted-foreground")}>{rem.title}</p>
                          {overdue && <Badge variant="destructive" className="text-[10px] h-4">Overdue</Badge>}
                          {rem.automationRuleId && <Badge variant="outline" className="text-[10px] h-4"><Zap className="h-2.5 w-2.5 mr-0.5" />Auto</Badge>}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span>Due: {new Date(rem.dueAt).toLocaleDateString()} {new Date(rem.dueAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                          <span>→ {rem.assignedTo}</span>
                          <Link to={getEntityLink(rem.entityType, rem.entityId)} className="text-primary hover:underline flex items-center gap-0.5">{rem.entityName} <ChevronRight className="h-3 w-3" /></Link>
                        </div>
                      </div>
                      {rem.status === "pending" && (
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-success hover:bg-success/10" onClick={() => completeReminder(rem.id)}><CheckCircle2 className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground" onClick={() => dismissReminder(rem.id)}><XCircle className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ─── LOG TAB ─── */}
        <TabsContent value="history" className="space-y-4">
          <p className="text-sm text-muted-foreground">{executions.length} total executions</p>
          <div className="space-y-2">
            {[...executions].sort((a, b) => new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime()).map((exec) => (
              <Card key={exec.id} className="border hover:shadow-sm cursor-pointer" onClick={() => setSelectedExec(exec)}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                        <p className="text-sm font-medium text-foreground">{exec.ruleName}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Triggered by <span className="font-medium">{exec.event.type.replace(/_/g, " ")}</span> on{" "}
                        <Link to={getEntityLink(exec.event.sourceModule === "pipeline" ? "lead" : exec.event.sourceModule === "quotes" ? "quote" : "job", exec.event.entityId)} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{exec.event.entityName}</Link>
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {exec.actions.map((action) => {
                          const AI = ACTION_ICONS[action.type] || Bell;
                          return <Badge key={action.id} variant="outline" className="text-[10px] gap-0.5"><AI className="h-2.5 w-2.5" />{action.description.slice(0, 40)}{action.description.length > 40 ? "…" : ""}</Badge>;
                        })}
                      </div>
                    </div>
                    <div className="text-right shrink-0 text-[10px] text-muted-foreground">
                      <p>{new Date(exec.executedAt).toLocaleDateString()}</p>
                      <p>{new Date(exec.executedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* ─── RULE EDITOR DIALOG ─── */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              {editorMode === "create" ? "Create Automation Rule" : "Edit Rule"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Basic info */}
            <div className="grid gap-3">
              <div>
                <Label className="text-xs font-semibold">Rule Name *</Label>
                <Input value={editingRule.name} onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })} className="h-9" placeholder="e.g., New Lead Follow-Up" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Description</Label>
                <Textarea value={editingRule.description} onChange={(e) => setEditingRule({ ...editingRule, description: e.target.value })} className="h-16" placeholder="What this rule does..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Category</Label>
                  <Select value={editingRule.category} onValueChange={(v: any) => setEditingRule({ ...editingRule, category: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Priority (1-10)</Label>
                  <Input type="number" min={1} max={10} value={editingRule.priority} onChange={(e) => setEditingRule({ ...editingRule, priority: Math.min(10, Math.max(1, parseInt(e.target.value) || 5)) })} className="h-9" />
                </div>
              </div>
            </div>

            <Separator />

            {/* WHEN — Trigger */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-info/15 text-info border-info/30 text-xs font-semibold">WHEN</Badge>
                <span className="text-xs text-muted-foreground">This event occurs</span>
              </div>
              <Select value={editingRule.eventType} onValueChange={(v: AutomationEventType) => setEditingRule({ ...editingRule, eventType: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.entries(EVENT_TYPE_LABELS) as [AutomationEventType, string][]).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* IF — Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-warning/15 text-warning border-warning/30 text-xs font-semibold">IF</Badge>
                  <span className="text-xs text-muted-foreground">These conditions are met (optional)</span>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addCondition}><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              {editingRule.conditions.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No conditions — rule triggers on every matching event</p>
              ) : (
                <div className="space-y-2">
                  {editingRule.conditions.map((cond) => (
                    <div key={cond.id} className="flex items-center gap-2 bg-muted/40 rounded-md p-2">
                      <Select value={cond.field} onValueChange={(v: ConditionField) => updateCondition(cond.id, { field: v })}>
                        <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(Object.entries(CONDITION_FIELD_LABELS) as [ConditionField, string][]).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={cond.operator} onValueChange={(v: ConditionOperator) => updateCondition(cond.id, { operator: v })}>
                        <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(Object.entries(CONDITION_OPERATOR_LABELS) as [ConditionOperator, string][]).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {cond.operator !== "exists" && cond.operator !== "not_exists" && (
                        <Input value={cond.value} onChange={(e) => updateCondition(cond.id, { value: e.target.value })} className="h-8 flex-1 text-xs" placeholder="Value" />
                      )}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => removeCondition(cond.id)}><XCircle className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* THEN — Actions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-success/15 text-success border-success/30 text-xs font-semibold">THEN</Badge>
                  <span className="text-xs text-muted-foreground">Execute these actions *</span>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addAction}><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              <div className="space-y-3">
                {editingRule.actions.map((action, idx) => (
                  <div key={action.id} className="bg-muted/40 rounded-md p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-muted-foreground">Action {idx + 1}</span>
                      {editingRule.actions.length > 1 && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => removeAction(action.id)}><XCircle className="h-3 w-3" /></Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">Action Type</Label>
                        <Select value={action.type} onValueChange={(v: AutomationActionType) => updateAction(action.id, { type: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(Object.entries(ACTION_TYPE_LABELS) as [AutomationActionType, string][]).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px]">Target</Label>
                        <Select value={action.target} onValueChange={(v) => updateAction(action.id, { target: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TARGET_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px]">Message Template *</Label>
                      <Input value={action.template} onChange={(e) => updateAction(action.id, { template: e.target.value })} className="h-8 text-xs" placeholder="Use {{entityName}}, {{newStage}}, etc." />
                    </div>
                    {(action.type === "create_reminder") && (
                      <div>
                        <Label className="text-[10px]">Delay (hours)</Label>
                        <Input type="number" value={action.delayHours || ""} onChange={(e) => updateAction(action.id, { delayHours: parseInt(e.target.value) || undefined })} className="h-8 text-xs w-24" placeholder="24" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveRule}>{editorMode === "create" ? "Create Rule" : "Save Changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── DELETE CONFIRM ─── */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete Rule?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This action cannot be undone. Consider archiving instead.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteConfirm && handleDelete(deleteConfirm)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── EXECUTION DETAIL ─── */}
      <Dialog open={!!selectedExec} onOpenChange={(open) => { if (!open) setSelectedExec(null); }}>
        <DialogContent className="max-w-md">
          {selectedExec && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4 text-primary" />{selectedExec.ruleName}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Trigger Event</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Type:</span> <span className="font-medium">{selectedExec.event.type.replace(/_/g, " ")}</span></div>
                    <div><span className="text-muted-foreground">Entity:</span> <span className="font-medium">{selectedExec.event.entityName}</span></div>
                    <div><span className="text-muted-foreground">Module:</span> <span className="font-medium capitalize">{selectedExec.event.sourceModule}</span></div>
                    <div><span className="text-muted-foreground">By:</span> <span className="font-medium">{selectedExec.event.triggeredBy}</span></div>
                  </div>
                </div>
                <Separator />
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Actions Executed</h4>
                  <div className="space-y-2">
                    {selectedExec.actions.map((action) => {
                      const AI = ACTION_ICONS[action.type] || Bell;
                      return (
                        <div key={action.id} className="flex items-start gap-2 text-sm">
                          <div className="p-1 rounded bg-success/15 mt-0.5"><AI className="h-3 w-3 text-success" /></div>
                          <div>
                            <p className="text-foreground">{action.description}</p>
                            <p className="text-xs text-muted-foreground">→ {action.target}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <Separator />
                <p className="text-xs text-muted-foreground">Executed at {new Date(selectedExec.executedAt).toLocaleString()}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
